/* eslint-disable react/no-unescaped-entities */
/**
 * Publicly accessible Privacy Policy — no auth required.
 * Content mirrors /app/memory/legal/PRIVACY_POLICY.md.
 */
import LegalLayout from "@/components/legal/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      testid="privacy-page"
      eyebrow="Privacy"
      title="HOH Solfa Studio — Privacy Policy"
      effective="July 2026"
      publisher="HOH VIBES"
      contact="support@hohvibes.com"
      website="https://hohvibes.com"
    >
      <p>
        HOH VIBES ("we", "our", "us") operates the HOH Solfa Studio mobile application and website
        (together the "Service"). This Privacy Policy explains what personal data we collect, why
        we collect it, and the choices you have.
      </p>

      <h2>1. What we collect</h2>

      <h3>Account information</h3>
      <ul>
        <li>Your email address, name and (optionally) profile photo, obtained from the sign-in provider you choose (Google or Apple).</li>
        <li>A stable internal user identifier we generate to identify you across devices.</li>
      </ul>

      <h3>Purchase information</h3>
      <ul>
        <li>The plan you purchased, the amount, currency, timestamp, and a payment-processor reference (Stripe on the web; Apple StoreKit on iOS via RevenueCat). We do not store your full card number, CVV or bank details — these are handled directly by the payment processor.</li>
      </ul>

      <h3>Usage &amp; content data</h3>
      <ul>
        <li>Hymns you mark as favourite, hymns you view, folders you create, admin uploads you make (admin users only).</li>
        <li>Language and display preferences.</li>
      </ul>

      <h3>Device diagnostics</h3>
      <ul>
        <li>Basic error logs and diagnostic events required to keep the app running reliably. We do not use third-party analytics or advertising SDKs.</li>
      </ul>

      <p>We do not track you across apps or websites, and we do not sell your personal data.</p>

      <h2>2. Why we collect it</h2>
      <table>
        <thead>
          <tr><th>Purpose</th><th>Legal basis</th></tr>
        </thead>
        <tbody>
          <tr><td>To create and authenticate your account</td><td>Contract</td></tr>
          <tr><td>To fulfil purchases and deliver premium content</td><td>Contract</td></tr>
          <tr><td>To keep the Service secure and prevent fraud</td><td>Legitimate interest</td></tr>
          <tr><td>To answer support requests</td><td>Legitimate interest</td></tr>
          <tr><td>To comply with tax and accounting obligations</td><td>Legal obligation</td></tr>
        </tbody>
      </table>

      <h2>3. Where your data is stored</h2>
      <p>
        Your data is stored on managed cloud infrastructure (MongoDB Atlas) located in the
        United Kingdom / European Union. Payment data is processed by Stripe, Inc. and Apple, Inc.
        under their own privacy policies.
      </p>

      <h2>4. How long we keep it</h2>
      <ul>
        <li><strong>Account data:</strong> while your account is active, and up to 30 days after you delete it.</li>
        <li><strong>Purchase records:</strong> 7 years after purchase, in an anonymised form, to satisfy UK HMRC / EU VAT / IRS financial-audit requirements. Personally-identifying fields are removed on account deletion.</li>
        <li><strong>Support emails:</strong> 24 months.</li>
      </ul>

      <h2>5. Your rights</h2>
      <p>Under UK GDPR and EU GDPR you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Correct inaccurate data.</li>
        <li>Ask us to delete your data ("right to erasure").</li>
        <li>Restrict or object to processing.</li>
        <li>Receive a portable copy of your data.</li>
        <li>Withdraw consent at any time.</li>
        <li>Complain to your local data-protection authority (in the UK, the Information Commissioner's Office, <a href="https://ico.org.uk/" target="_blank" rel="noreferrer noopener">https://ico.org.uk/</a>).</li>
      </ul>
      <p>
        You can delete your account and all associated personal data at any time from within the app:
        open the <strong>Profile tab → Danger Zone → Delete my account</strong>. Anonymised financial
        records may be retained as described above.
      </p>

      <h2>6. Children</h2>
      <p>
        HOH Solfa Studio is not directed at children under 13. We do not knowingly collect personal
        data from anyone under 13. If you believe a child has provided us data, please contact{" "}
        <a href="mailto:support@hohvibes.com">support@hohvibes.com</a> and we will delete it promptly.
      </p>

      <h2>7. Cookies (website only)</h2>
      <p>
        Our website uses only strictly-necessary cookies for session management. The mobile app uses
        secure on-device storage (Apple Keychain / Android Keystore) to keep you signed in — no cookies
        are set on your device by the app.
      </p>

      <h2>8. Changes to this policy</h2>
      <p>
        We will post any material changes to this policy at <a href="https://hohvibes.com/privacy">https://hohvibes.com/privacy</a> and
        notify signed-in users via an in-app message. Continued use of the Service after a change means
        you accept the updated policy.
      </p>

      <h2>9. Contact</h2>
      <p>Questions or requests about your data can be sent to:</p>
      <p>
        <strong>HOH VIBES — Data Protection</strong><br />
        Email: <a href="mailto:support@hohvibes.com">support@hohvibes.com</a><br />
        Web: <a href="https://hohvibes.com">https://hohvibes.com</a>
      </p>
    </LegalLayout>
  );
}
